import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { LuDot, LuMoreVertical } from 'react-icons/lu';
import { SITELINK } from '../../../app-routes/Links';
import { getFirstLetters, getUser, truncateText, tinyAlert } from '../../../app-utils';
import { Verify } from '../../../assets/custom-icons';
import LazyLoad from 'react-lazyload';
import { useLikeFeedById } from '../../../api-services/feeds/useLikeFeedById';
import Modal from '../../../components/modal';
import { useSaveFeedById } from '../../../api-services/feeds/useSaveFeedById';
import { UserInterface } from '../../../app-schemas/profile.schema';
import { useDeleteFeedById } from '../../../api-services/feeds/useDeleteFeeds';
import { PostInterface } from '../../../app-schemas/posts/post.schema';
import { RiBookmarkFill, RiBookmarkLine } from 'react-icons/ri';
import LinkPreview from '../../../components/LinkPreview';
import { useVoteByIndex } from '../../../api-services/feeds/useVote';
import { useTheme } from '../../../context/theme-context';
import { useUserCommunities } from '../../../api-services/community/useUserCommunities';
import feeds_api from '../../../api-services/feeds/feeds.services';
import { LuX } from 'react-icons/lu';

interface PostCardProps {
  post: PostInterface;
  index: number;
  innerRef?: React.Ref<HTMLDivElement>;
}

const formatDate = (dateStr?: string): string => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return dateStr;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

// ─── Post Type Config ────────────────────────────────────────────────────────

const POST_TYPE_CONFIG: Record<  string,  { label: string; bg: string; text: string; icon: React.ReactNode }> = {
  job: {
    label: 'Job',
    bg: 'bg-blue-50',
    text: 'text-blue-600',
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  market: {
    label: 'Market',
    bg: 'bg-green-50',
    text: 'text-green-600',
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
        />
      </svg>
    ),
  },
  events: {
    label: 'Event',
    bg: 'bg-purple-50',
    text: 'text-purple-600',
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  poll: {
    label: 'Poll',
    bg: 'bg-yellow-50',
    text: 'text-yellow-600',
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
        />
      </svg>
    ),
  },
  task: {
    label: 'Task',
    bg: 'bg-orange-50',
    text: 'text-orange-600',
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4"
        />
      </svg>
    ),
  },
  rent: {
    label: 'Rent',
    bg: 'bg-rose-50',
    text: 'text-rose-600',
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
        />
      </svg>
    ),
  },
  rides: {
    label: 'Ride',
    bg: 'bg-cyan-50',
    text: 'text-cyan-600',
    icon: (
      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
        />
      </svg>
    ),
  },
};

// ─── Type-specific detail renderers ─────────────────────────────────────────

const JobDetails: React.FC<{ post: PostInterface }> = ({ post }) => (
  <div className="mt-2 mb-3 rounded-md border border-blue-100 bg-blue-50/50 dark:bg-blue-200 p-2 space-y-1.5 text-[13px]">
    {post.title && <p className="font-semibold text-gray-900 text-[14px]">{post.title}</p>}
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-600">
      {post.location && (
        <span className="flex items-center gap-1">
          <svg
            className="w-3.5 h-3.5 text-blue-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          {post.location}
        </span>
      )}
      {post.amount > 0 && (
        <span className="flex items-center gap-1 font-medium text-blue-700">
          {post.currency}{post.amount.toLocaleString()}
          {post.frequency ? `/${post.frequency}` : ''}
        </span>
      )}
      {post.start_date && (
        <span className="flex items-center gap-1">
          <svg
            className="w-3.5 h-3.5 text-blue-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          Starts {formatDate(post.start_date)}
        </span>
      )}
      {post.deadline_date && (
        <span className="text-red-500 flex items-center gap-1">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Deadline: {formatDate(post.deadline_date)}
        </span>
      )}
    </div>
  </div>
);

const MarketDetails: React.FC<{ post: PostInterface }> = ({ post }) => (
  <div className="mt-2 mb-3 rounded-md border border-green-100 bg-green-50/50 dark:bg-green-200 p-3 text-[13px]">
    {post.title && <p className="font-semibold text-gray-900 text-[14px] mb-1">{post.title}</p>}
    {post.amount > 0 && (
      <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 font-semibold px-2.5 py-1 rounded-full text-[13px]">
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        {post.currency}{post.amount.toLocaleString()}
      </span>
    )}
  </div>
);

const EventDetails: React.FC<{ post: PostInterface }> = ({ post }) => (
  <div className="mt-2 mb-3 rounded-md border border-purple-100 bg-purple-50/50 dark:bg-purple-200 p-2 space-y-1.5 text-[13px]">
    {post.title && <p className="font-semibold text-gray-900 text-[14px]">{post.title}</p>}
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-600">
      {post.location && (
        <span className="flex items-center gap-1">
          <svg
            className="w-3.5 h-3.5 text-purple-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          {post.location}
        </span>
      )}
      {post.start_date && (
        <span className="flex items-center gap-1 text-purple-700 font-medium">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          {formatDate(post.start_date)}
        </span>
      )}
      {post.deadline_date && (
        <span className="flex items-center gap-1">
          <svg
            className="w-3.5 h-3.5 text-purple-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Ends {formatDate(post.deadline_date)}
        </span>
      )}
    </div>
  </div>
);

// ─── PollDetails ─────────────────────────────────────────────────────────────

const PollDetails: React.FC<{ post: PostInterface }> = ({ post }) => {
  const { mutate: vote, isPending } = useVoteByIndex();

  // Prefer poll_results (rich data with votes) — fall back to poll_options (plain strings)
  const displayOptions = useMemo(() => {
    if (post.poll_results && post.poll_results.length > 0) {
      return post.poll_results;
    }

    return (post.poll_options ?? []).map((label) => ({
      label,
      votes: 0,
      has_voted: false,
    }));
  }, [post.poll_results, post.poll_options]);

  const totalVotes = useMemo(
    () => displayOptions.reduce((a, b) => a + b.votes, 0),
    [displayOptions],
  );

  const hasVoted = useMemo(() => displayOptions.some((opt) => opt.has_voted), [displayOptions]);

  const maxVotes = useMemo(
    () => Math.max(...displayOptions.map((o) => o.votes), 0),
    [displayOptions],
  );

  const handleVote = (index: number) => {
    if (isPending) return;
    vote({ postId: post.post_id, index });
  };

  const getPercent = (votes: number): number => {
    if (totalVotes === 0) return 0;
    return Math.round((votes / totalVotes) * 100);
  };

  return (
    <div className="mt-2 mb-3 rounded-md border border-yellow-100 bg-yellow-50/50 dark:bg-yellow-200 p-3 text-[13px]">
      {post.title && <p className="font-semibold text-gray-900 text-[14px] mb-3">{post.title}</p>}

      <div className="space-y-2">
        {displayOptions.map((option, i) => {
          const percent = getPercent(option.votes);
          const isSelected = option.has_voted;
          const winning = option.votes === maxVotes && maxVotes > 0;

          return (
            <button
              key={i}
              onClick={() => handleVote(i)}
              disabled={isPending}
              className={`relative w-full rounded-lg overflow-hidden text-left transition-all border ${
                hasVoted ? 'cursor-default' : 'cursor-pointer hover:border-yellow-400'
              } ${isSelected ? 'border-yellow-400 bg-white' : 'border-yellow-200 bg-white'}`}
            >
              {/* Progress bar fill — only shown after voting */}
              {hasVoted && (
                <div
                  className={`absolute inset-0 transition-all duration-700 ease-out rounded-lg ${
                    isSelected ? 'bg-yellow-100' : 'bg-gray-50'
                  }`}
                  style={{ width: `${percent}%` }}
                />
              )}

              <div className="relative flex items-center justify-between px-3 py-2 gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {/* Radio indicator */}
                  <span
                    className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${
                      isSelected
                        ? 'border-yellow-500 bg-yellow-500'
                        : hasVoted
                        ? 'border-gray-300'
                        : 'border-yellow-400'
                    }`}
                  >
                    {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                  </span>

                  <span
                    className={`font-medium truncate ${
                      isSelected ? 'text-yellow-800' : 'text-gray-700'
                    }`}
                  >
                    {option.label}
                  </span>

                  {hasVoted && winning && (
                    <span className="text-[10px] font-semibold text-yellow-600 bg-yellow-100 border border-yellow-200 px-1.5 py-0.5 rounded-full flex-shrink-0">
                      Leading
                    </span>
                  )}
                </div>

                {hasVoted && (
                  <span
                    className={`text-[12px] font-semibold flex-shrink-0 ${
                      isSelected ? 'text-yellow-700' : 'text-gray-500'
                    }`}
                  >
                    {percent}%
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-2.5 flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-black">
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
        {totalVotes === 0
          ? 'No votes yet — be the first!'
          : `${totalVotes} vote${totalVotes !== 1 ? 's' : ''}`}
        {hasVoted && <span className="ml-1 text-yellow-500 font-medium">· You voted</span>}
        {isPending && (
          <span className="ml-1 text-yellow-400 font-medium animate-pulse">· Saving...</span>
        )}
      </div>
    </div>
  );
};

const TaskDetails: React.FC<{ post: PostInterface }> = ({ post }) => (
  <div className="mt-2 mb-3 rounded-md border border-orange-100 bg-orange-50/50 dark:bg-orange-200 p-2 space-y-1.5 text-[13px]">
    {post.title && <p className="font-semibold text-gray-900 text-[14px]">{post.title}</p>}
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-600">
      {post.task_type && (
        <span className="inline-flex items-center gap-1 bg-orange-100 text-orange-700 font-medium px-2 py-0.5 rounded-full">
          {post.task_type}
        </span>
      )}
      {post.amount > 0 && (
        <span className="flex items-center gap-1 font-semibold text-orange-700">
          {post.currency}{post.amount.toLocaleString()}
        </span>
      )}
      {post.start_date && (
        <span className="flex items-center gap-1">
          <svg
            className="w-3.5 h-3.5 text-orange-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          {formatDate(post.start_date)}
        </span>
      )}
      {post.deadline_date && (
        <span className="flex items-center gap-1 text-red-500">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          Due {formatDate(post.deadline_date)}
        </span>
      )}
    </div>
  </div>
);

const RentDetails: React.FC<{ post: PostInterface }> = ({ post }) => (
  <div className="mt-2 mb-3 rounded-md border border-rose-100 bg-rose-50/50 dark:bg-rose-200 p-2 space-y-1.5 text-[13px]">
    {post.title && <p className="font-semibold text-gray-900 text-[14px]">{post.title}</p>}
    <div className="flex flex-wrap gap-x-4 gap-y-1 text-gray-600">
      {post.location && (
        <span className="flex items-center gap-1">
          <svg
            className="w-3.5 h-3.5 text-rose-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          {post.location}
        </span>
      )}
      {post.amount > 0 && (
        <span className="flex items-center gap-1 font-semibold text-rose-700">
          {post.currency}{post.amount.toLocaleString()}
          <span className="font-normal text-gray-500">/yr</span>
        </span>
      )}
    </div>
  </div>
);

const RideDetails: React.FC<{ post: PostInterface }> = ({ post }) => {
  const formatTime12hr = (time?: string): string => {
    if (!time) return '';
    const [hourStr, minuteStr] = time.split(':');
    const hour = parseInt(hourStr, 10);
    const minute = minuteStr ? parseInt(minuteStr, 10) : 0;
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;
  };

  const isDriver = post.ride_user_type === 'driver';

  return (
    <div className="mt-2 mb-3 rounded-md border border-cyan-100 bg-cyan-50/50 dark:bg-cyan-200 p-2 space-y-2 text-[13px]">
      <div className="flex items-center gap-2">
        {post.ride_user_type && (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
              isDriver ? 'bg-cyan-100 text-cyan-700' : 'bg-teal-100 text-teal-700'
            }`}
          >
            {isDriver ? '🚗 Offering Ride' : '🙋 Needs Ride'}
          </span>
        )}
        {post.amount > 0 && (
          <span className="font-semibold text-cyan-700">
            {post.currency}{post.amount.toLocaleString()}
            {isDriver && (
              <span className="font-normal text-gray-500 text-[11px] ml-1">available</span>
            )}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1 text-gray-600">
        {post.location && (
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
            <span className="font-medium text-gray-700">From:</span> {post.location}
          </span>
        )}
        {post.destination && (
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
            <span className="font-medium text-gray-700">To:</span> {post.destination}
          </span>
        )}
        {(post.start_date || post.trip_time) && (
          <span className="flex items-center gap-1.5 text-cyan-700 font-medium">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {post.start_date && formatDate(post.start_date)}
            {post.start_date && post.trip_time && ' · '}
            {post.trip_time && formatTime12hr(post.trip_time)}
          </span>
        )}
      </div>
    </div>
  );
};

// Loading Component
const LoadingPercentage = ({ percentage }: { percentage: number }) => {
  return (
    <div className="flex items-center justify-center gap-2">
      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      <span>{Math.round(percentage)}%</span>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const PostCard: React.FC<PostCardProps> = ({ post, index, innerRef }) => {
  console.log(post);

  const likeFeedById = useLikeFeedById();
  const saveFeedById = useSaveFeedById();
  const deleteFeedById = useDeleteFeedById();
  const user: UserInterface = getUser();

  const { isDarkMode } = useTheme();

  const [openSetupModal, setOpenSetupModal] = useState<boolean>(false);
  const [openRepostModal, setOpenRepostModal] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<string | null>();
  const [selectedCommunities, setSelectedCommunities] = useState<string[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [repostError, setRepostError] = useState<string>('');

  const userCommunities = useUserCommunities();
  const userCommunitiesOptions = useMemo(() => {
    if (userCommunities.isSuccess && userCommunities?.data?.data?.data) {
      return userCommunities.data.data.data.map((community: any) => ({
        id: community.id,
        name: community.name,
        city: community.city,
        country: community.country,
      }));
    }
    return [];
  }, [userCommunities.isSuccess, userCommunities.data]);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess('Copied!');
      setTimeout(() => setCopySuccess(null), 2000);
    } catch {
      setCopySuccess(null);
    }
  };

  const handleOpenSetup = () => {
    if (openSetupModal) {
      document.body.classList.remove('overflow-hidden');
    } else {
      document.body.classList.add('overflow-hidden');
    }
    setCopySuccess(null);
    setOpenSetupModal(!openSetupModal);
  };

  const handleToggleSetupModal = useCallback(() => {
    document.body.classList.toggle('overflow-hidden', !openSetupModal);
    setOpenSetupModal((prev) => !prev);
  }, [openSetupModal]);

  const handleLikeFeedById = useCallback(() => {
    likeFeedById.mutate(post.post_id);
  }, [likeFeedById, post.post_id]);

  const handleSaveFeed = useCallback(() => {
    saveFeedById.mutate(post.post_id);
  }, [saveFeedById, post.post_id]);

  const handleDeleteFeed = useCallback(() => {
    deleteFeedById.mutate(post.post_id);
    handleToggleSetupModal();
  }, [deleteFeedById, post.post_id, handleToggleSetupModal]);

  const handleShareFeed = useCallback(() => {
    if (navigator.share) {
      navigator
        .share({ title: 'Catapot', text: 'my post from catapot.com', url: 'catapot.com' })
        .then(() => console.log('Share successful'))
        .catch((error) => console.error('Error sharing:', error));
    }
  }, []);

  // Handle Repost
  const handleRepost = () => {
    // Close the main menu modal
    setOpenSetupModal(false);
    document.body.classList.remove('overflow-hidden');
    
    // Reset states
    setSelectedCommunities([]);
    setRepostError('');
    
    // Open the repost community selection modal
    setOpenRepostModal(true);
    document.body.classList.add('overflow-hidden');
  };

  const handleCloseRepostModal = () => {
    setOpenRepostModal(false);
    document.body.classList.remove('overflow-hidden');
    setSelectedCommunities([]);
    setUploadProgress(0);
    setRepostError('');
  };

  const handleCheckboxChange = (communityId: string) => {
    setSelectedCommunities((prevSelected) => {
      if (prevSelected.includes(communityId)) {
        return prevSelected.filter((item) => item !== communityId);
      } else {
        return [...prevSelected, communityId];
      }
    });
    // Clear error when user selects a community
    if (repostError) setRepostError('');
  };

  const handleSubmitRepost = async () => {
    if (selectedCommunities.length === 0) {
      setRepostError('Select at least one community');
      return;
    }

    setLoading(true);
    setUploadProgress(0);

    // Simulate progress for better UX
    let simulatedProgress = 0;
    const ticker = setInterval(() => {
      simulatedProgress += Math.random() * 8;
      if (simulatedProgress < 85) {
        setUploadProgress(Math.round(simulatedProgress));
      }
    }, 300);

    try {
      const payload = {
        community_post_id: post.post_id,
        community_ids: selectedCommunities.map(id => parseInt(id)),
      };

      // Call the existing repost method
      await feeds_api.repost(payload);

      clearInterval(ticker);
      setUploadProgress(100);

      tinyAlert('success', 'Post reposted successfully!');
      
      setTimeout(() => {
        handleCloseRepostModal();
        setLoading(false);
      }, 600);
    } catch (error) {
      clearInterval(ticker);
      setUploadProgress(0);
      console.error('Repost error:', error);
      tinyAlert('error', 'Failed to repost. Please try again.');
      setLoading(false);
    }
  };

  const truncatedContent = useMemo(() => {
    const textWithoutFirstHashtag = (post?.content ?? '').replace(/^#\S+\s*/, '');
    return truncateText(textWithoutFirstHashtag, 135);
  }, [post?.content]);

  const typeConfig = post?.post_type ? POST_TYPE_CONFIG[post.post_type] : null;
  const showBadge = typeConfig && post.post_type !== 'tweet';

  const profileImage = post?.creator?.profile_picture_url ? (
    <LazyLoad height={200} offset={1000} once>
      <img
        alt="profile"
        src={post?.creator?.profile_picture_url}
        className="object-cover rounded-full w-[45px] h-[42px] bg-gray-200 shadow-sm"
      />
    </LazyLoad>
  ) : (
    <span
      className="flex items-center justify-center w-[45px] h-[45px] rounded-full border border-green-100"
      style={{ background: '#5c5c5c' }}
    >
      <h3 className="text-white uppercase text-[2rem] font-[600]">
        <span>{getFirstLetters(post?.creator?.username)}</span>
      </h3>
    </span>
  );

  const renderImages = useMemo(
    () =>
      post?.images?.map((image, idx) => (
        <LazyLoad height={200} offset={1000} once key={idx}>
          <div
            className="absolute inset-0 bg-cover bg-center blur-3xl w-[45px] h-[40px]"
            style={{ backgroundImage: `url(${image})` }}
          />
          <img src={image} className="relative w-full z-10" alt={`post_image ${idx}`} />
        </LazyLoad>
      )),
    [post?.images],
  );

  const renderTypeDetails = () => {
    switch (post?.post_type) {
      case 'job':
        return <JobDetails post={post} />;
      case 'market':
        return <MarketDetails post={post} />;
      case 'events':
        return <EventDetails post={post} />;
      case 'poll':
        return <PollDetails post={post} />;
      case 'task':
        return <TaskDetails post={post} />;
      case 'rent':
        return <RentDetails post={post} />;
      case 'rides':
        return <RideDetails post={post} />;
      default:
        return null;
    }
  };

  return (
    <div key={index} className="bg-white dark:bg-neutral-800 pt-2 border-b last:border-0" ref={innerRef}>
      <div className="flex gap-2 justify-between p-3 pb-0">
        <div className="flex gap-2 items-start w-full">
          {/* Avatar */}
          <div className="w-[50px] flex-shrink-0">
            <Link to={`${SITELINK.MY_PROFILE}/${post?.creator?.username}`}>{profileImage}</Link>
          </div>

          <div className="w-full min-w-0">
            {/* Header row */}
            <div className="flex justify-between w-full">
              <div className="min-w-0 flex-1">
                <div className="text-[16px] text-gray-800 dark:text-white font-[400] flex gap-1 madimi-one flex-wrap items-center">
                  <Link to={`${SITELINK.MY_PROFILE}/${post?.creator?.username}`}>
                    <span className="mr-1">{post?.creator?.first_name}</span>
                    <span>{post?.creator?.last_name}</span>
                  </Link>
                  <span className="text-gray-500 dark:text-gray-300">
                    @
                    {post?.creator?.username
                      ? truncateText(post?.creator?.username, 5)
                      : 'Anonymous'}
                  </span>
                  {post?.creator?.is_verified && (
                    <span>
                      <img src={Verify} alt="" className="h-[12px]" />
                    </span>
                  )}
                  <span className="text-gray-500 dark:text-white flex items-center">
                    <LuDot />
                    {post?.created_on}
                  </span>
                </div>

                <div className="flex items-center gap-2 mt-0.5">
                  <Link
                    to={`${SITELINK.COMMUNITIES}/activities/${post?.community_id}`}
                    className="text-[12px] text-gray-500 font-[500] dark:text-white/80"
                  >
                    {post?.community}
                  </Link>
                  {showBadge && (
                    <span
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${typeConfig.bg} ${typeConfig.text}`}
                    >
                      {typeConfig.icon}
                      {typeConfig.label}
                    </span>
                  )}
                </div>
              </div>

              <div className="flex-shrink-0 flex flex-col items-end gap-1">
                <button className="flex justify-center text-end" onClick={handleOpenSetup}>
                  <LuMoreVertical className="text-xl text-gray-700 dark:text-white" />
                </button>
                {post?.is_boosted && (
                  <span className="rounded-lg bg-white border border-gray-300 font-bold text-gray-500 px-1 text-[8px]">
                    Sponsored
                  </span>
                )}
              </div>
            </div>

            {/* Type-specific details */}
            {renderTypeDetails()}

            {/* Text Content */}
            {post?.content && post.content !== 'undefined' && (
              <div className="text-[16px] tracking-[-0.5px] mb-3 text-black dark:text-white font-[500] mt-2 text-sm">
                <Link
                  to={`${SITELINK.VIEW_POSTS}/${post.post_id}`}
                  className="break-words whitespace-normal inline-block max-w-full min-w-0"
                >
                  <LinkPreview url={post?.content} />
                  {truncatedContent}
                  {post?.content?.length > 134 && (
                    <span className="text-gray-600 text-[14px] dark:text-white">&nbsp;&nbsp;See more</span>
                  )}
                </Link>
              </div>
            )}
            {/* Audio */}
            {post?.audio && post?.audio?.length > 0 && (
              <audio
                controls
                src={post.audio[0]}
                className="w-full custom-audio-player px-3 pb-3"
              />
            )}

            {/* Images */}
            {post?.images?.length > 0 && (
              <Link
                to={`${SITELINK.VIEW_POSTS}/${post.post_id}`}
                className="relative flex justify-center items-center overflow-hidden bg-cover bg-center max-h-[250px] rounded-xl"
              >
                {renderImages}
              </Link>
            )}

            {/* Action bar */}
            <div className="pt-1">
              <div className="flex gap-5 font-medium text-[12px]">
                {/* Comment */}
                <div>
                  <Link
                    to={`${SITELINK.VIEW_POSTS}/${post.post_id}`}
                    className="flex items-center px-2 py-2"
                  >
                    <svg
                      width="16"
                      height="15"
                      viewBox="0 0 16 15"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        clipRule="evenodd"
                        d="M7 10.25C7.27614 10.25 7.5 10.4739 7.5 10.75V12.7079C10.2487 11.1024 11.9025 9.96659 12.8999 8.97494C13.4787 8.39946 13.7971 7.9087 13.982 7.44074C14.1679 6.97021 14.25 6.44448 14.25 5.75C14.25 3.26472 12.2353 1.25 9.75 1.25H6.25C3.76472 1.25 1.75 3.26472 1.75 5.75C1.75 8.23528 3.76472 10.25 6.25 10.25H7ZM7.5 14.1529C14.1641 10.3177 15.5 8.74691 15.5 5.75C15.5 2.57436 12.9256 0 9.75 0H6.25C3.07436 0 0.5 2.57436 0.5 5.75C0.5 8.92564 3.07436 11.5 6.25 11.5V14.0086C6.25 14.3911 6.66196 14.6318 6.9943 14.4424C7.16618 14.3445 7.33473 14.248 7.5 14.1529Z"
                        fill={isDarkMode ? "#fff" : "#687684"}
                      />
                    </svg>
                    {post?.comments > 0 && (
                      <span className="pl-1 pr-1 text-[12px] text-[#687684] dark:text-white">{post?.comments}</span>
                    )}
                  </Link>
                </div>

                {/* Like */}
                <div>
                  <button
                    className={`flex items-center px-2 py-2 ${
                      post?.reacted_to_post ? 'text-custom-600' : 'text-[#687684]'
                    }`}
                    onClick={handleLikeFeedById}
                  >
                    {post?.reacted_to_post ? (
                      <svg
                        width="15"
                        height="14"
                        viewBox="0 0 15 14"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M3.301 0.116975C4.20936 -0.110107 5.16825 -0.00392529 6.00114 0.412501L6.34439 0.584118C6.75885 0.791337 7.12315 1.07457 7.42092 1.41413C7.46262 1.46168 7.53738 1.46168 7.57908 1.41413C7.87685 1.07457 8.24115 0.791337 8.65561 0.584118L8.99886 0.412501C9.83175 -0.00392529 10.7906 -0.110107 11.699 0.116975C12.572 0.335201 13.3339 0.844888 13.8627 1.54996L14.0553 1.80682C14.6706 2.62707 15 3.61989 15 4.6363V4.92317C15 5.37799 14.9442 5.83129 14.8338 6.27303L14.7666 6.54171C14.5139 7.55255 14.0612 8.50474 13.4332 9.34191L13.0419 9.86374C12.7272 10.2833 12.3829 10.6803 12.0115 11.0517L11.6146 11.4486C10.6621 12.401 9.56086 13.1951 8.35236 13.7993C7.81719 14.0669 7.18281 14.0669 6.64764 13.7993C5.43914 13.1951 4.33786 12.401 3.38544 11.4486L2.98849 11.0517C2.61709 10.6803 2.27281 10.2833 1.95814 9.86374L1.56675 9.34191C0.938842 8.50474 0.486109 7.55255 0.233388 6.54171L0.166213 6.27303C0.0557751 5.83129 0 5.37799 0 4.92317V4.6363C0 3.61989 0.329438 2.62707 0.944651 1.80682L1.1373 1.54996C1.66613 0.844888 2.42805 0.335201 3.301 0.116975Z"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="15"
                        height="14"
                        viewBox="0 0 15 14"
                        fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M3.301 0.116975C4.20936 -0.110107 5.16825 -0.00392529 6.00114 0.412501L6.34439 0.584118C6.75885 0.791337 7.12315 1.07457 7.42092 1.41413C7.46262 1.46168 7.53738 1.46168 7.57908 1.41413C7.87685 1.07457 8.24115 0.791337 8.65561 0.584118L8.99886 0.412501C9.83175 -0.00392529 10.7906 -0.110107 11.699 0.116975C12.572 0.335201 13.3339 0.844888 13.8627 1.54996L14.0553 1.80682C14.6706 2.62707 15 3.61989 15 4.6363V4.92317C15 5.37799 14.9442 5.83129 14.8338 6.27303L14.7666 6.54171C14.5139 7.55255 14.0612 8.50474 13.4332 9.34191L13.0419 9.86374C12.7272 10.2833 12.3829 10.6803 12.0115 11.0517L11.6146 11.4486C10.6621 12.401 9.56086 13.1951 8.35236 13.7993C7.81719 14.0669 7.18281 14.0669 6.64764 13.7993C5.43914 13.1951 4.33786 12.401 3.38544 11.4486L2.98849 11.0517C2.61709 10.6803 2.27281 10.2833 1.95814 9.86374L1.56675 9.34191C0.938842 8.50474 0.486109 7.55255 0.233388 6.54171L0.166213 6.27303C0.0557751 5.83129 0 5.37799 0 4.92317V4.6363C0 3.61989 0.329438 2.62707 0.944651 1.80682L1.1373 1.54996C1.66613 0.844888 2.42805 0.335201 3.301 0.116975ZM5.42056 1.42404C4.85999 1.14377 4.22215 1.07469 3.6191 1.22544C3.03787 1.37074 2.51507 1.71404 2.14454 2.20805L1.95189 2.46491C1.48501 3.08738 1.22951 3.84931 1.22951 4.6363V4.92317C1.22951 5.29055 1.27457 5.65634 1.36356 6.01226L1.43073 6.28095C1.64855 7.15217 2.03785 7.96901 2.57399 8.68382L2.96537 9.20565C3.24739 9.58165 3.55551 9.93686 3.88731 10.2686L4.28426 10.6656C5.14673 11.528 6.14105 12.2442 7.22822 12.7878C7.40072 12.874 7.59928 12.874 7.77178 12.7878C8.85895 12.2442 9.85327 11.528 10.7157 10.6656L11.1127 10.2686C11.4445 9.93686 11.7526 9.58165 12.0346 9.20565L12.426 8.68382C12.9621 7.96901 13.3515 7.15217 13.5693 6.28095L13.6364 6.01226C13.7254 5.65634 13.7705 5.29055 13.7705 4.92317V4.6363C13.7705 3.84931 13.515 3.08738 13.0481 2.46491L12.8555 2.20805C12.4849 1.71404 11.9621 1.37074 11.3809 1.22544C10.7779 1.07469 10.14 1.14377 9.57944 1.42404L9.23619 1.59566C8.7923 1.8176 8.42422 2.18181 8.19478 2.64068C7.78499 3.46022 7.21238 3.45495 6.80522 2.64068C6.57578 2.18181 6.2077 1.8176 5.76381 1.59566L5.42056 1.42404Z"
                          fill={isDarkMode ? "#fff" : "#687684"}
                        />
                      </svg>
                    )}
                    <span className="pl-1 pr-1 text-[12px] dark:text-white">
                      {post?.reactions > 0 ? post?.reactions : ''}
                    </span>
                  </button>
                </div>

                {/* Share */}
                <div>
                  <button className="flex items-center px-2 py-2" onClick={handleShareFeed}>
                    <svg
                      width="16"
                      height="13"
                      viewBox="0 0 16 13"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M14.325 6.00002L10.5 2.17502V4.22502L9.78333 4.33335C6.19167 4.84169 3.75833 6.72502 2.36667 9.60835C4.3 8.24169 6.7 7.58335 9.66667 7.58335H10.5V9.82502M8.83333 8.43335C5.10833 8.60835 2.44167 9.95002 0.5 12.6667C1.33333 8.50002 3.83333 4.33335 9.66667 3.50002V0.166687L15.5 6.00002L9.66667 11.8334V8.41669C9.39167 8.41669 9.11667 8.42502 8.83333 8.43335Z"
                        fill={isDarkMode ? "#fff" : "#687684"}
                      />
                    </svg>
                  </button>
                </div>

                {/* Save */}
                <div>
                  <button className="flex items-center px-2 py-2" onClick={handleSaveFeed}>
                    {post?.bookmarked_post ? (
                      <RiBookmarkFill className="text-[1rem] text-custom-600 mr-1" />
                    ) : (
                      <RiBookmarkLine className="text-[1rem] text-[#687684] mr-1 dark:text-white" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="pb-3">
              <Link
                to={`${SITELINK.COMMUNITIES}/activities/${post?.community_id}`}
                className="text-[14px] text-[#1877F2] font-medium"
              >
                Show this in the Community
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Main Menu Modal */}
      <Modal isOpen={openSetupModal} onClose={handleOpenSetup}>
        <div className="w-[90vw] max-w-md mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden text-sm">
          <div className="py-2">
            <button 
              onClick={handleRepost}
              className="flex items-center w-full px-6 py-1.5 hover:bg-gray-50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center mr-4 group-hover:bg-gray-100 transition-colors">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                  />
                </svg>
              </div>
              <p className="font-medium text-gray-900">Repost</p>
            </button>

            {post.creator.email === user.email && (
              <button
                onClick={handleDeleteFeed}
                className="flex items-center w-full px-6 py-1.5 hover:bg-red-50 transition-colors group"
              >
                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center mr-4 group-hover:bg-red-100 transition-colors">
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                    />
                  </svg>
                </div>
                <p className="font-medium text-red-600">Delete post</p>
              </button>
            )}

            <button
              onClick={() =>
                copyToClipboard(
                  `${post?.creator?.username ?? 'Anonymous'} made a post into ${
                    post?.community
                  }\nview post: https://${window.location.host}${SITELINK.VIEW_POSTS}/${
                    post.post_id
                  }\nJoin and meet with people in your closest community.`,
                )
              }
              className="flex items-center w-full px-6 py-1.5 hover:bg-gray-50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center mr-4 group-hover:bg-gray-100 transition-colors">
                {copySuccess ? (
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                ) : (
                  <svg
                    className="w-5 h-5 text-gray-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                )}
              </div>
              <p className="font-medium text-gray-900">{copySuccess || 'Copy link'}</p>
            </button>

            <button
              onClick={handleSaveFeed}
              className="flex items-center w-full px-6 py-1.5 hover:bg-gray-50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center mr-4 group-hover:bg-gray-100 transition-colors">
                {post?.bookmarked_post ? (
                  <RiBookmarkFill className="w-5 h-5 text-gray-600" />
                ) : (
                  <RiBookmarkLine className="w-5 h-5 text-gray-600" />
                )}
              </div>
              <p className="font-medium text-gray-900">
                {post?.bookmarked_post ? 'Unsave post' : 'Save post'}
              </p>
            </button>

            <Link
              to={`${SITELINK.VIEW_POSTS}/${post.post_id}`}
              className="flex items-center w-full px-6 py-1.5 hover:bg-gray-50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center mr-4 group-hover:bg-gray-100 transition-colors">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </div>
              <p className="font-medium text-gray-900">View post</p>
            </Link>

            <button
              onClick={handleShareFeed}
              className="flex items-center w-full px-6 py-1.5 hover:bg-gray-50 transition-colors group"
            >
              <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center mr-4 group-hover:bg-gray-100 transition-colors">
                <svg
                  className="w-5 h-5 text-gray-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z"
                  />
                </svg>
              </div>
              <p className="font-medium text-gray-900">Share post</p>
            </button>
          </div>
        </div>
      </Modal>

      {/* Repost Community Selection Modal - Matching Composer Style */}
      <Modal isOpen={openRepostModal} onClose={handleCloseRepostModal}>
        <section className="w-[90vw] bg-white rounded-2xl shadow-2xl overflow-hidden">
          <div className="px-4 py-3 border-b flex justify-between items-center">
            <h3 className="text-lg font-semibold">Select communities</h3>
            <button onClick={handleCloseRepostModal} className="p-1 hover:bg-gray-100 rounded-full">
              <LuX className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          <div className="px-1 overflow-y-scroll h-[200px]">
            {userCommunitiesOptions.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>You don't have any communities yet.</p>
                <Link 
                  to={SITELINK.COMMUNITIES} 
                  className="text-custom-600 hover:underline text-sm mt-2 inline-block"
                  onClick={handleCloseRepostModal}
                >
                  Join or create a community
                </Link>
              </div>
            ) : (
              <div className="">
                {userCommunitiesOptions.map((community: any, idx: number) => (
                  <div 
                    key={idx} 
                    className="flex items-center gap-3 px-2 hover:bg-gray-50 transition-colors cursor-pointer border-b"
                    onClick={() => handleCheckboxChange(community.id.toString())}
                  >
                    <input
                      type="checkbox"
                      id={`repost_community_${idx}`}
                      checked={selectedCommunities.includes(community.id.toString())}
                      onChange={() => handleCheckboxChange(community.id.toString())}
                      className="w-4 h-4 text-custom-600 rounded border-gray-300 focus:ring-custom-500 cursor-pointer"
                    />
                    <label htmlFor={`repost_community_${idx}`} className="flex-1 cursor-pointer">
                      <p className="font-medium text-gray-900 text-sm">{community.name}</p>
                    </label>
                  </div>
                ))}
              </div>
            )}
          </div>

          {selectedCommunities.length > 0 && (
            <div className="px-4 py-2 bg-gray-50 border-t border-b">
              <p className="text-xs text-gray-600">
                Selected {selectedCommunities.length} community{selectedCommunities.length !== 1 ? 'ies' : ''}
              </p>
            </div>
          )}

          {repostError && (
            <div className="px-4 py-2 bg-red-50 border-t border-b border-red-100">
              <p className="text-xs text-red-600">{repostError}</p>
            </div>
          )}

          <div className="p-4 bg-white">
            <button
              onClick={handleSubmitRepost}
              disabled={loading || selectedCommunities.length === 0}
              className="w-full py-3 rounded-lg bg-custom-600 text-white hover:bg-custom-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
            >
              {loading ? <LoadingPercentage percentage={uploadProgress} /> : 'Repost'}
            </button>
          </div>
        </section>
      </Modal>
    </div>
  );
};

export default PostCard;